import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';

/**
 * Badge — un estado, siempre con su palabra.
 *
 * La regla dura: **nunca solo color**. Alrededor del 8 % de los hombres no
 * distingue el verde del rojo, así que una insignia que solo cambia de tono no
 * comunica nada para uno de cada doce lectores. El texto no es el complemento
 * del color: es el dato, y el color lo acompaña.
 *
 * El patrón es tinte de fondo + texto oscuro del MISMO tono. Se estrenó en
 * Solicitudes y funciona en los dos temas. Lo que NO se puede hacer —y se hizo
 * durante meses— es pintar el texto con el mismo color del fondo tintado
 * (`background: c22; color: c`): eso daba 1,85 de contraste en «Pendiente»,
 * «Aprobada» y «Rechazada».
 *
 * Radio 4 px, no píldora. La píldora se reserva a los filtros, que son
 * pulsables; una insignia no se pulsa, y darles la misma forma invita a
 * intentarlo.
 */
const insignia = cva(
  'inline-flex items-center gap-1.5 rounded-xs px-2 py-0.5 text-1 font-semibold ' +
    'leading-normal whitespace-nowrap',
  {
    variants: {
      tone: {
        neutral: 'bg-sunken text-ink-2',
        info: 'bg-info/12 text-info-ink',
        ok: 'bg-ok/12 text-ok-ink',
        warn: 'bg-warn/16 text-warn-ink',
        danger: 'bg-danger/12 text-danger-ink',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
);

export function Badge({
  tone, className, children, ...props
}: React.HTMLAttributes<HTMLSpanElement> & VariantProps<typeof insignia>) {
  return (
    <span className={cn(insignia({ tone }), className)} {...props}>
      {children}
    </span>
  );
}
